var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _pendingRequestUrl, _abortController, _checkedIndices, _radios, _resizeObserver, _VariantPicker_instances, getFieldsetMeasurements_fn, applyFieldsetMeasurements_fn;
import { Component } from "@theme/component";
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph, MORPH_OPTIONS } from "@theme/morph";
import { OverflowList } from "@theme/overflow-list";
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from "@theme/utilities";
class VariantPicker extends Component {
  constructor() {
    super(...arguments);
    __privateAdd(this, _VariantPicker_instances);
    /** @type {string | undefined} */
    __privateAdd(this, _pendingRequestUrl);
    /** @type {AbortController | undefined} */
    __privateAdd(this, _abortController);
    /** @type {number[][]} */
    __privateAdd(this, _checkedIndices, []);
    /** @type {HTMLInputElement[][]} */
    __privateAdd(this, _radios, []);
    __privateAdd(this, _resizeObserver, new ResizeNotifier(() => this.updateVariantPickerCss()));
  }
  connectedCallback() {
    super.connectedCallback();
    const fieldsets = (
      /** @type {HTMLFieldSetElement[]} */
      this.refs.fieldsets || []
    );
    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll("input") ?? []);
      __privateGet(this, _radios).push(radios);
      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === "true");
      if (initialCheckedIndex !== -1) {
        __privateGet(this, _checkedIndices).push([initialCheckedIndex]);
      }
    });
    this.addEventListener("change", this.variantChanged.bind(this));
    __privateGet(this, _resizeObserver).observe(this);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    __privateGet(this, _resizeObserver).disconnect();
  }
  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const selectedOption = event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;
    if (!selectedOption) return;
    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? ""
    }));
    const isOnProductPage = this.dataset.templateProductMatch === "true" && !event.target.closest("product-card") && !event.target.closest("quick-add-dialog");
    const currentUrl = this.dataset.productUrl?.split("?")[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest("featured-product-information"));
    const morphElementSelector = loadsNewProduct ? "main" : isOnFeaturedProductSection ? "featured-product-information" : void 0;
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);
    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId || null;
    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set("variant", variantId);
      } else {
        url.searchParams.delete("variant");
      }
    }
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }
    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, "", url.toString());
      });
    }
  }
  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;
    const measurements = __privateMethod(this, _VariantPicker_instances, getFieldsetMeasurements_fn).call(this, fieldsetIndex);
    if (measurements) {
      __privateMethod(this, _VariantPicker_instances, applyFieldsetMeasurements_fn).call(this, measurements);
    }
  }
  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === "string") {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!targetElement) throw new Error("Target element not found");
      target = targetElement;
    }
    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || "");
      const inputIndex = Number.parseInt(target.dataset.inputIndex || "");
      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = (
          /** @type {HTMLFieldSetElement[]} */
          this.refs.fieldsets || []
        );
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = __privateGet(this, _checkedIndices)[fieldsetIndex];
        const radios = __privateGet(this, _radios)[fieldsetIndex];
        if (radios && checkedIndices && fieldset) {
          const [currentIndex, previousIndex] = checkedIndices;
          if (currentIndex !== void 0 && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = "false";
          }
          if (previousIndex !== void 0 && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = "false";
          }
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);
          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];
          if (newCurrentIndex !== void 0 && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = "true";
          }
          if (newPreviousIndex !== void 0 && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = "true";
            radios[newPreviousIndex].dataset.currentChecked = "false";
          }
          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);
      if (!newSelectedOption) throw new Error("Option not found");
      for (const option of target.options) {
        option.removeAttribute("selected");
      }
      newSelectedOption.setAttribute("selected", "selected");
    }
  }
  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || __privateGet(this, _pendingRequestUrl) || this.dataset.productUrl;
    __privateSet(this, _pendingRequestUrl, productUrl);
    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push(`view=${viewParamValue}`);
    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
    } else if (source === "product-card") {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(",")}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }
    const SECTION_ID_MAP = {
      "quick-add-component": "section-rendering-product-card",
      "swatches-variant-picker-component": "section-rendering-product-card",
      "featured-product-information": this.closest("featured-product-information")?.id
    };
    const closestSectionId = (
      /** @type {keyof typeof SECTION_ID_MAP} | undefined */
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );
    if (closestSectionId) {
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join("&")}`;
    }
    return `${productUrl}?${params.join("&")}`;
  }
  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    __privateGet(this, _abortController)?.abort();
    __privateSet(this, _abortController, new AbortController());
    fetch(requestUrl, { signal: __privateGet(this, _abortController).signal }).then((response) => response.text()).then((responseText) => {
      __privateSet(this, _pendingRequestUrl, void 0);
      const html = new DOMParser().parseFromString(responseText, "text/html");
      html.querySelector("overflow-list[defer]")?.removeAttribute("defer");
      const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
      if (!textContent) return;
      let newProduct;
      if (morphElementSelector === "main") {
        this.updateMain(html);
      } else if (morphElementSelector) {
        this.updateElement(html, morphElementSelector);
      } else {
        const { overflowList } = this.refs;
        const wasSwatchesExpanded = overflowList instanceof OverflowList && overflowList.getAttribute("disabled") === "true";
        newProduct = this.updateVariantPicker(html);
        if (wasSwatchesExpanded) {
          const overflowListAfterMorph = overflowList;
          if (overflowListAfterMorph instanceof OverflowList) {
            overflowListAfterMorph.showAll();
          }
        }
      }
      if (this.selectedOptionId) {
        this.dispatchEvent(
          new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
            html,
            productId: this.dataset.productId ?? "",
            newProduct
          })
        );
      }
    }).catch((error) => {
      if (error.name === "AbortError") {
        console.warn("Fetch aborted by user");
      } else {
        console.error(error);
      }
    });
  }
  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */
  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    let newProduct;
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;
      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }
      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }
    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return void 0;
        const key = node.dataset.key;
        return key;
      }
    });
    this.updateVariantPickerCss();
    return newProduct;
  }
  updateVariantPickerCss() {
    const fieldsets = (
      /** @type {HTMLFieldSetElement[]} */
      this.refs.fieldsets || []
    );
    const measurements = fieldsets.map((_, index) => __privateMethod(this, _VariantPicker_instances, getFieldsetMeasurements_fn).call(this, index)).filter((m) => m !== null);
    for (const measurement of measurements) {
      __privateMethod(this, _VariantPicker_instances, applyFieldsetMeasurements_fn).call(this, measurement);
    }
  }
  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);
    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }
    morph(element, newElement);
  }
  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector("main");
    const newMain = newHtml.querySelector("main");
    if (!main || !newMain) {
      throw new Error("No new main source found");
    }
    morph(main, newMain);
  }
  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector("select option[selected], fieldset input:checked");
    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return void 0;
    }
    return selectedOption;
  }
  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return void 0;
    const { optionValueId } = selectedOption.dataset;
    if (!optionValueId) {
      throw new Error("No option value ID found");
    }
    return optionValueId;
  }
  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll("select option[selected], fieldset input:checked"));
    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error("No option value ID found");
      return optionValueId;
    });
  }
}
_pendingRequestUrl = new WeakMap();
_abortController = new WeakMap();
_checkedIndices = new WeakMap();
_radios = new WeakMap();
_resizeObserver = new WeakMap();
_VariantPicker_instances = new WeakSet();
/**
 * @typedef {object} FieldsetMeasurements
 * @property {HTMLFieldSetElement} fieldset
 * @property {number | undefined} currentIndex
 * @property {number | undefined} previousIndex
 * @property {number | undefined} currentWidth
 * @property {number | undefined} previousWidth
 */
/**
 * Gets measurements for a single fieldset (read phase).
 * @param {number} fieldsetIndex
 * @returns {FieldsetMeasurements | null}
 */
getFieldsetMeasurements_fn = function(fieldsetIndex) {
  const fieldsets = (
    /** @type {HTMLFieldSetElement[]} */
    this.refs.fieldsets || []
  );
  const fieldset = fieldsets[fieldsetIndex];
  const checkedIndices = __privateGet(this, _checkedIndices)[fieldsetIndex];
  const radios = __privateGet(this, _radios)[fieldsetIndex];
  if (!radios || !checkedIndices || !fieldset) return null;
  const [currentIndex, previousIndex] = checkedIndices;
  return {
    fieldset,
    currentIndex,
    previousIndex,
    currentWidth: currentIndex !== void 0 ? radios[currentIndex]?.parentElement?.offsetWidth : void 0,
    previousWidth: previousIndex !== void 0 ? radios[previousIndex]?.parentElement?.offsetWidth : void 0
  };
};
/**
 * Applies measurements to a fieldset (write phase).
 * @param {FieldsetMeasurements} measurements
 */
applyFieldsetMeasurements_fn = function({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
  if (currentWidth) {
    fieldset.style.setProperty("--pill-width-current", `${currentWidth}px`);
  } else if (currentIndex !== void 0) {
    fieldset.style.removeProperty("--pill-width-current");
  }
  if (previousWidth) {
    fieldset.style.setProperty("--pill-width-previous", `${previousWidth}px`);
  } else if (previousIndex !== void 0) {
    fieldset.style.removeProperty("--pill-width-previous");
  }
};
if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}
export {
  VariantPicker as default
};
